import { ArrowUpRight, Play } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

export default function Hero() {
  const { t } = useLang();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative pt-28 pb-24 lg:pt-36 lg:pb-32 overflow-hidden"
    >
      {/* Subtle grid */}
      <div className="absolute inset-0 bbm-grid-bg opacity-60 pointer-events-none" />
      {/* Halos */}
      <div className="bbm-halo bg-[#0047FF] w-[400px] h-[400px] top-[-100px] start-[-100px]" />
      <div className="bbm-halo bg-[#FFB800] w-[300px] h-[300px] top-[40%] end-[-50px]" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        {/* Text */}
        <div className="lg:col-span-7 bbm-fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#E5E7EB] bg-white/70 mb-8" data-testid="hero-eyebrow">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FFB800]" />
            <span className="font-mono text-[11px] uppercase tracking-widest text-[#0B132B]">
              {t.hero.eyebrow}
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-[#0B132B] leading-[0.98] tracking-tight mb-6" data-testid="hero-title">
            {t.hero.title_pre}{" "}
            <span className="relative whitespace-nowrap">
              <span className="text-[#0047FF]">{t.hero.title_em}</span>
              <svg className="absolute -bottom-2 start-0 w-full" viewBox="0 0 200 8" preserveAspectRatio="none" aria-hidden="true">
                <path d="M0 5 Q 50 0, 100 5 T 200 5" stroke="#FFB800" strokeWidth="2.5" fill="none" />
              </svg>
            </span>{" "}
            {t.hero.title_post}
          </h1>

          <p className="text-lg lg:text-xl text-[#4B5563] max-w-2xl leading-relaxed mb-10" data-testid="hero-sub">
            {t.hero.sub}
          </p>

          <div className="flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollTo("contact")}
              className="bbm-btn-primary"
            >
              {t.hero.cta_primary}
              <ArrowUpRight className="w-4 h-4" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollTo("work")}
              className="bbm-btn-secondary"
            >
              <Play className="w-3.5 h-3.5" />
              {t.hero.cta_secondary}
            </button>
          </div>

          <p className="mt-10 text-xs font-mono uppercase tracking-widest text-[#4B5563]">
            <span className="inline-block w-8 h-[1px] bg-[#4B5563] align-middle me-3" />
            {t.hero.trust}
          </p>
        </div>

        {/* Visual */}
        <div className="lg:col-span-5 relative bbm-fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-[#E5E7EB] bg-[#0B132B]">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/7d38956f-74c5-4266-a29e-5f2f50e06042/images/f1ec0e45bdee97a563e1188b017114fd00aa1a00b1fa5918d0b61f81298786f9.png"
              alt="AI and CRM networking"
              className="w-full h-full object-cover opacity-90"
              data-testid="hero-image"
            />
            {/* Overlay info card */}
            <div className="absolute bottom-4 start-4 end-4 bg-white/95 backdrop-blur rounded-lg p-4 border border-white/40 shadow-xl">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#4B5563]">Live · ops dashboard</div>
                  <div className="font-display text-lg text-[#0B132B] mt-0.5">+38% revenue lift</div>
                </div>
                <div className="text-end">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#4B5563]">90 days</div>
                  <div className="font-display text-lg text-[#0047FF] mt-0.5">$2.4M</div>
                </div>
              </div>
            </div>
          </div>

          {/* floating accent badge */}
          <div className="absolute -top-4 -end-4 bg-[#FFB800] text-[#0B132B] rounded-full px-4 py-2 font-mono text-xs uppercase tracking-widest shadow-lg hidden sm:block">
            US · CN · DZ
          </div>
        </div>
      </div>
    </section>
  );
}
